import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LanguageSwitch } from "../components/LanguageSwitch";
import { acceptInvitation } from "../lib/api";
import { useI18n } from "../lib/i18n";

const strongPassword =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/;

export function AcceptInvitationPage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [activatedEmail, setActivatedEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (password !== confirmPassword) {
      setError(t("passwordMismatch"));
      return;
    }
    if (!strongPassword.test(password)) {
      setError(t("passwordSetupFailed"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await acceptInvitation({ token, displayName, password });
      setActivatedEmail(result.email);
    } catch {
      setError(t("invitationFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <div className="login-language">
        <LanguageSwitch />
      </div>
      <section className="login-panel">
        <div className="login-brand">
          <img
            className="login-logo"
            src="/bridge-ai-logo.png"
            alt={t("appTagline")}
          />
        </div>
        <div className="login-copy">
          <span className="eyebrow">{t("appName")}</span>
          <h1>{t("activateAccount")}</h1>
          <p>{activatedEmail ? t("accountActivated") : t("invitationHelp")}</p>
        </div>
        {activatedEmail ? (
          <div className="activation-success">
            <strong>{activatedEmail}</strong>
            <Link className="primary-button full-width" to="/login">
              {t("goToSignIn")}
            </Link>
          </div>
        ) : token.length !== 64 ? (
          <>
            <p className="error" role="alert">
              {t("invitationInvalid")}
            </p>
            <Link className="secondary-button full-width" to="/login">
              {t("goToSignIn")}
            </Link>
          </>
        ) : (
          <form onSubmit={(event) => void submit(event)}>
            <label>
              {t("displayName")}
              <input
                autoComplete="name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                required
                minLength={2}
              />
            </label>
            <label>
              {t("newPassword")}
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={12}
              />
            </label>
            <label>
              {t("confirmPassword")}
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                minLength={12}
              />
            </label>
            <p className="field-hint">{t("setPasswordHelp")}</p>
            {error && (
              <p className="error" role="alert">
                {error}
              </p>
            )}
            <button
              className="primary-button full-width"
              type="submit"
              disabled={busy}
            >
              {t("activateAccount")}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
